import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { X, Inbox, Undo2, Lock } from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { dismissPartnerOnboarding } from "../lib/api";

// One-time, contextual JOIN moment for an INVITED partner (role "member"). Shown once,
// dismissible, non-blocking. Trigger: member + has partner + not yet dismissed.
// Owners, solo users, and existing/already-dismissed users never see it.
export default function PartnerJoinSheet() {
  const { user, refresh } = useAuth();
  const navigate = useNavigate();
  const [closing, setClosing] = useState(false);

  const name = user?.partner?.name;
  const inviter = name || "your partner";
  const Inviter = name || "Your partner";
  const show = user?.role === "member" && Boolean(user?.partner) && !user?.partner_onboarding_seen;
  if (!show || closing) return null;

  const dismiss = async (goInbox) => {
    setClosing(true);                       // hide immediately — never blocks app use
    try { await dismissPartnerOnboarding(); await refresh(); } catch (_) { /* persists best-effort */ }
    if (goInbox) navigate("/inbox");
  };

  const Line = ({ icon: Icon, children }) => (
    <li className="flex items-start gap-3">
      <span className="h-7 w-7 rounded-full bg-[var(--pine-soft)] text-[var(--pine)] flex items-center justify-center shrink-0 mt-0.5">
        <Icon size={14} strokeWidth={1.9} />
      </span>
      <span className="text-[14px] text-[var(--ink2)] leading-snug">{children}</span>
    </li>
  );

  return (
    <div
      data-testid="partner-join-moment"
      className="fixed inset-0 z-[60] flex items-end sm:items-center justify-center p-3 sm:p-4 pb-safe bg-[rgba(28,38,34,0.34)] backdrop-blur-sm rise-in"
      onClick={() => dismiss(false)}
    >
      <div
        className="relative w-full max-w-md rounded-3xl bg-[var(--surface)] border border-[var(--line)] shadow-[0_24px_70px_rgba(28,38,34,0.30)] p-6 sm:p-7"
        onClick={(e) => e.stopPropagation()}
      >
        <button
          data-testid="partner-join-close"
          onClick={() => dismiss(false)}
          aria-label="Dismiss"
          className="absolute top-4 right-4 h-8 w-8 rounded-full flex items-center justify-center text-[var(--ink4)] hover:text-[var(--ink2)] hover:bg-[var(--surface2)] transition-colors"
        >
          <X size={17} strokeWidth={2} />
        </button>

        <h2 className="text-2xl font-semibold text-[var(--ink)] tracking-[-0.01em] pr-8" data-testid="partner-join-headline">
          {Inviter} invited you to share the load
        </h2>
        <p className="text-[15px] text-[var(--ink2)] mt-2.5 leading-relaxed">
          Heldall is a quiet place for the things you do not want to keep in your head. When {inviter} hands
          something to you, it will land in your Inbox. You can keep it, give it back with a reason, or mark it done.
        </p>

        <ul className="mt-5 space-y-3">
          <Line icon={Inbox}>Things handed to you appear in Inbox.</Line>
          <Line icon={Undo2}>You can give something back if it is not yours or you cannot do it.</Line>
          <Line icon={Lock}>Private items stay private unless they are handed over.</Line>
        </ul>

        <div className="mt-7 flex items-center gap-2.5">
          <button
            data-testid="partner-join-got-it"
            onClick={() => dismiss(false)}
            className="inline-flex items-center justify-center rounded-full bg-[var(--pine)] hover:bg-[var(--pine-dark)] text-[var(--on-pine)] px-5 py-2.5 text-sm font-semibold transition-all shadow-[0_6px_18px_rgba(44,90,77,0.22)]"
          >
            Got it
          </button>
          <button
            data-testid="partner-join-go-inbox"
            onClick={() => dismiss(true)}
            className="inline-flex items-center justify-center rounded-full px-4 py-2.5 text-sm font-semibold text-[var(--ink3)] hover:text-[var(--ink2)] transition-colors"
          >
            Go to Inbox
          </button>
        </div>
      </div>
    </div>
  );
}
